import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, fileUrl, FALLBACK_COVER } from "@/lib/api";
import { GENRES, slugify, genreColor } from "@/lib/genres";
import { usePlayer } from "@/context/PlayerContext";
import { PlayButton, EmptyState } from "@/components/site/ReleaseCard";
import { Eq } from "@/components/site/CdjPlayerBar";

const asRelease = (t) => ({ id: t.release_id, title: t.release_title, artist_name: t.artist_name, cover_url: t.cover_url, tracks: [t] });

const Row = ({ t, i }) => {
  const { current, playing } = usePlayer();
  const isCur = current?.id === t.id;
  return (
    <div className={`grid grid-cols-[2rem_3.5rem_1fr_auto] md:grid-cols-[2rem_3.5rem_2fr_1.2fr_4rem_3.5rem_6rem] items-center gap-3 px-3 py-2.5 rounded-xl ${isCur ? "bg-emerald-500/10 ring-1 ring-emerald-400/30" : "hover:bg-white/5"} transition-colors`} data-testid={`genre-track-row-${i}`}>
      <span className="font-mono-tga text-xs text-zinc-500">{i + 1}</span>
      <div className="relative h-14 w-14 rounded-md overflow-hidden group">
        <img src={fileUrl(t.cover_url) || FALLBACK_COVER} alt="" className="h-full w-full object-cover" />
        {t.audio_url && <div className="absolute inset-0 grid place-items-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"><PlayButton release={asRelease(t)} trackIdx={0} testid={`genre-track-play-${i}`} className="h-9 w-9" /></div>}
      </div>
      <div className="min-w-0">
        <Link to={`/uscite/${t.release_id}`} className={`font-semibold truncate block hover:text-emerald-300 ${isCur ? "text-emerald-300" : ""}`}>{t.title}</Link>
        <p className="text-sm text-zinc-400 truncate">{t.artist_name}</p>
      </div>
      <p className="hidden md:block text-sm text-zinc-300 truncate">{t.release_title}</p>
      <p className="hidden md:block font-mono-tga text-xs text-zinc-300">{t.bpm || "—"}</p>
      <p className="hidden md:block font-mono-tga text-xs text-zinc-300">{t.key || "—"}</p>
      <div className="flex items-center justify-end gap-2">
        {isCur && <Eq active={playing} />}
        <span className="font-mono-tga text-xs text-zinc-500 hidden sm:inline">{t.release_date?.slice(0, 10)}</span>
      </div>
    </div>
  );
};

export default function GenreDetail() {
  const { slug } = useParams();
  const [tracks, setTracks] = useState(null);
  useEffect(() => { api.get("/tracks", { params: { genre: slug } }).then((r) => setTracks(r.data)).catch(() => setTracks([])); }, [slug]);
  const name = GENRES.find((g) => slugify(g) === slug) || tracks?.[0]?.genre || slug;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-28" data-testid="genre-detail-page">
      <Link to="/generi" className="inline-flex items-center gap-2 font-mono-tga text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white" data-testid="genre-back-link"><ArrowLeft className="h-4 w-4" /> Generi</Link>
      <div className="mt-6 mb-10 flex items-center gap-4">
        <span className="h-12 w-2 rounded-full" style={{ background: genreColor(name) }} />
        <div>
          <p className="font-mono-tga text-xs uppercase tracking-[0.3em] text-emerald-400">Genere</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter" data-testid="genre-title">{name}</h1>
        </div>
      </div>
      <div className="hidden md:grid grid-cols-[2rem_3.5rem_2fr_1.2fr_4rem_3.5rem_6rem] gap-3 px-3 pb-2 font-mono-tga text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/10">
        <span>#</span><span /><span>Titolo / Artista</span><span>Uscita</span><span>BPM</span><span>Key</span><span className="text-right">Data</span>
      </div>
      {tracks === null ? <p className="py-10 text-zinc-500">Caricamento…</p> : tracks.length ? (
        <div className="space-y-1 mt-2" data-testid="genre-track-list">{tracks.map((t, i) => <Row key={t.id} t={t} i={i} />)}</div>
      ) : <div className="mt-6"><EmptyState testid="genre-empty" title="Nessun brano in questo genere" text="Presto nuove uscite." /></div>}
    </div>
  );
}
